(() => {

  console.log('PRODUCT GALLERY JS LOADED');

  class ProductGallery {

    constructor(container) {

      this.container = container;

      /* =====================================================
         MAIN GALLERY
      ====================================================== */

      this.viewport =
        container.querySelector(
          '.product-gallery__viewport'
        );

      this.track =
        container.querySelector(
          '[data-gallery-track]'
        );

      this.slides =
        container.querySelectorAll(
          '[data-gallery-slide]'
        );

      this.prevButton =
        container.querySelector(
          '[data-gallery-prev]'
        );

      this.nextButton =
        container.querySelector(
          '[data-gallery-next]'
        );

      /* =====================================================
         THUMBNAILS
      ====================================================== */

      this.thumbnailViewport =
        container.querySelector(
          '[data-thumbnail-viewport]'
        );

      this.thumbnailTrack =
        container.querySelector(
          '[data-thumbnail-track]'
        );

      this.thumbnails =
        container.querySelectorAll(
          '[data-thumbnail]'
        );

      this.thumbPrevButton =
        container.querySelector(
          '[data-thumbnail-prev]'
        );

      this.thumbNextButton =
        container.querySelector(
          '[data-thumbnail-next]'
        );

      /* =====================================================
         LIGHTBOX
      ====================================================== */

      this.lightbox =
        container.querySelector(
          '[data-product-lightbox]'
        );

      this.lightboxTrack =
        container.querySelector(
          '[data-lightbox-track]'
        );

      this.lightboxClose =
        container.querySelector(
          '[data-lightbox-close]'
        );

      this.lightboxPrev =
        container.querySelector(
          '[data-lightbox-prev]'
        );

      this.lightboxNext =
        container.querySelector(
          '[data-lightbox-next]'
        );

      /* =====================================================
         STATE
      ====================================================== */

      this.currentIndex = 0;

      this.totalSlides =
        this.slides.length;

      this.thumbnailOffset = 0;

      /* =====================================================
         TOUCH
      ====================================================== */

      this.touchStartX = 0;
      this.touchCurrentX = 0;

      this.thumbTouchStartX = 0;
      this.thumbStartOffset = 0;

      this.init();

    }

    /* =====================================================
       INIT
    ====================================================== */

    init() {

      if (
        !this.viewport ||
        !this.track ||
        !this.slides.length
      ) {

        console.warn(
          'Gallery elements missing'
        );

        return;

      }

      this.bindEvents();

      this.update(false);

    }

    /* =====================================================
       EVENTS
    ====================================================== */

    bindEvents() {

      /* ===================================================
         MAIN ARROWS
      ==================================================== */

      this.prevButton?.addEventListener(
        'click',
        () => {

          this.prevSlide();

        }
      );

      this.nextButton?.addEventListener(
        'click',
        () => {

          this.nextSlide();

        }
      );

      /* ===================================================
         THUMBNAILS
      ==================================================== */

      this.thumbnails.forEach(
        (thumbnail, index) => {

          thumbnail.addEventListener(
            'click',
            () => {

              this.goToSlide(index);

            }
          );

        }
      );

      /* ===================================================
         THUMBNAIL ARROWS
      ==================================================== */

      this.thumbPrevButton?.addEventListener(
        'click',
        () => {

          this.scrollThumbnails(-1);

        }
      );

      this.thumbNextButton?.addEventListener(
        'click',
        () => {

          this.scrollThumbnails(1);

        }
      );

      /* ===================================================
         TOUCH SWIPE — MAIN GALLERY
      ==================================================== */

      this.viewport?.addEventListener(
        'touchstart',
        (event) => {

          this.touchStartX =
            event.touches[0].clientX;

        },
        { passive: true }
      );

      this.viewport?.addEventListener(
        'touchmove',
        (event) => {

          this.touchCurrentX =
            event.touches[0].clientX;

        },
        { passive: true }
      );

      this.viewport?.addEventListener(
        'touchend',
        () => {

          const deltaX =
            this.touchStartX -
            this.touchCurrentX;

          if (
            Math.abs(deltaX) < 50
          ) {
            return;
          }

          if (deltaX > 0) {

            this.nextSlide();

          } else {

            this.prevSlide();

          }

        }
      );

      /* ===================================================
         TOUCH DRAG — THUMBNAILS
      ==================================================== */

      this.thumbnailViewport?.addEventListener(
        'touchstart',
        (event) => {

          if (
            window.innerWidth > 1024
          ) {
            return;
          }

          this.thumbTouchStartX =
            event.touches[0].clientX;

          this.thumbStartOffset =
            this.thumbnailOffset;

        },
        { passive: true }
      );

      this.thumbnailViewport?.addEventListener(
        'touchmove',
        (event) => {

          if (
            window.innerWidth > 1024
          ) {
            return;
          }

          const currentX =
            event.touches[0].clientX;

          const deltaX =
            this.thumbTouchStartX -
            currentX;

          const maxOffset =
            this.thumbnailTrack.scrollWidth -
            this.thumbnailViewport.clientWidth;

          this.thumbnailOffset =
            this.thumbStartOffset +
            deltaX;

          /* ===============================================
             LIMITS
          =============================================== */

          if (
            this.thumbnailOffset < 0
          ) {

            this.thumbnailOffset = 0;

          }

          if (
            this.thumbnailOffset >
            maxOffset
          ) {

            this.thumbnailOffset =
              maxOffset;

          }

          this.thumbnailTrack.style.transform =
            `translateX(-${this.thumbnailOffset}px)`;

        },
        { passive: true }
      );

      /* ===================================================
         LIGHTBOX OPEN
      ==================================================== */

      this.slides.forEach(
        (slide, index) => {

          slide.addEventListener(
            'click',
            () => {

              this.openLightbox(index);

            }
          );

        }
      );

      /* ===================================================
         LIGHTBOX CLOSE
      ==================================================== */

      this.lightboxClose?.addEventListener(
        'click',
        () => {

          this.closeLightbox();

        }
      );

      /* ===================================================
         LIGHTBOX ARROWS
      ==================================================== */

      this.lightboxPrev?.addEventListener(
        'click',
        () => {

          this.prevSlide();

          this.updateLightbox();

        }
      );

      this.lightboxNext?.addEventListener(
        'click',
        () => {

          this.nextSlide();

          this.updateLightbox();

        }
      );

      /* ===================================================
         ESC CLOSE
      ==================================================== */

      document.addEventListener(
        'keydown',
        (event) => {

          if (
            event.key === 'Escape'
          ) {

            this.closeLightbox();

          }

        }
      );

      /* ===================================================
         RESIZE
      ==================================================== */

      window.addEventListener(
        'resize',
        () => {

          this.update(false);

        }
      );

    }

    /* =====================================================
       PREV
    ====================================================== */

    prevSlide() {

      if (
        this.currentIndex <= 0
      ) {

        this.fadeLoopReset(
          this.totalSlides - 1
        );

        return;

      }

      this.currentIndex--;

      this.update();

      this.updateLightbox();

    }

    /* =====================================================
       NEXT
    ====================================================== */

    nextSlide() {

      if (
        this.currentIndex >=
        this.totalSlides - 1
      ) {

        this.fadeLoopReset(0);

        return;

      }

      this.currentIndex++;

      this.update();

      this.updateLightbox();

    }

    /* =====================================================
       GO TO SLIDE
    ====================================================== */

    goToSlide(index) {

      this.currentIndex = index;

      this.update();

      this.updateLightbox();

    }

    /* =====================================================
       LOOP RESET
    ====================================================== */

    fadeLoopReset(targetIndex) {

      this.viewport.classList.add(
        'is-fading'
      );

      setTimeout(() => {

        this.currentIndex =
          targetIndex;

        this.update();

        this.updateLightbox();

        setTimeout(() => {

          this.viewport.classList.remove(
            'is-fading'
          );

        }, 50);

      }, 120);

    }

    /* =====================================================
       THUMBNAIL SCROLL
    ====================================================== */

    scrollThumbnails(direction) {

      if (
        !this.thumbnailTrack ||
        !this.thumbnailViewport
      ) return;

      const thumbnail =
        this.thumbnails[0];

      if (!thumbnail) return;

      const isMobile =
        window.innerWidth <= 1024;

      const gap =
        isMobile ? 8 : 10;

      const step =
        isMobile
          ? thumbnail.offsetWidth + gap
          : thumbnail.offsetHeight + gap;

      const maxOffset =
        isMobile
          ? this.thumbnailTrack.scrollWidth -
            this.thumbnailViewport.clientWidth
          : this.thumbnailTrack.scrollHeight -
            this.thumbnailViewport.clientHeight;

      this.thumbnailOffset +=
        direction * step;

      /* ===============================================
         LIMITS
      =============================================== */

      if (
        this.thumbnailOffset < 0
      ) {

        this.thumbnailOffset = 0;

      }

      if (
        this.thumbnailOffset >
        maxOffset
      ) {

        this.thumbnailOffset =
          maxOffset;

      }

      this.thumbnailTrack.style.transform =
        isMobile
          ? `translateX(-${this.thumbnailOffset}px)`
          : `translateY(-${this.thumbnailOffset}px)`;

    }

    /* =====================================================
       ACTIVE THUMB AUTO FOLLOW
    ====================================================== */

    scrollActiveThumbnailIntoView() {

      if (
        !this.thumbnailTrack ||
        !this.thumbnailViewport
      ) return;

      const activeThumbnail =
        this.thumbnails[
          this.currentIndex
        ];

      if (!activeThumbnail) return;

      const isMobile =
        window.innerWidth <= 1024;

      const gap =
        isMobile ? 8 : 10;

      const thumbSize =
        isMobile
          ? activeThumbnail.offsetWidth + gap
          : activeThumbnail.offsetHeight + gap;

      const thumbStart =
        this.currentIndex * thumbSize;

      const thumbEnd =
        thumbStart + thumbSize;

      const viewportStart =
        this.thumbnailOffset;

      const viewportEnd =
        viewportStart +
        (
          isMobile
            ? this.thumbnailViewport.clientWidth
            : this.thumbnailViewport.clientHeight
        );

      /* ===============================================
         AFTER VIEWPORT
      =============================================== */

      if (
        thumbEnd > viewportEnd
      ) {

        this.thumbnailOffset =
          thumbEnd -
          (
            isMobile
              ? this.thumbnailViewport.clientWidth
              : this.thumbnailViewport.clientHeight
          );

      }

      /* ===============================================
         BEFORE VIEWPORT
      =============================================== */

      if (
        thumbStart < viewportStart
      ) {

        this.thumbnailOffset =
          thumbStart;

      }

      const maxOffset =
        isMobile
          ? this.thumbnailTrack.scrollWidth -
            this.thumbnailViewport.clientWidth
          : this.thumbnailTrack.scrollHeight -
            this.thumbnailViewport.clientHeight;

      if (
        this.thumbnailOffset < 0
      ) {

        this.thumbnailOffset = 0;

      }

      if (
        this.thumbnailOffset >
        maxOffset
      ) {

        this.thumbnailOffset =
          maxOffset;

      }

      this.thumbnailTrack.style.transform =
        isMobile
          ? `translateX(-${this.thumbnailOffset}px)`
          : `translateY(-${this.thumbnailOffset}px)`;

    }

    /* =====================================================
       LIGHTBOX OPEN
    ====================================================== */

    openLightbox(index) {

      this.currentIndex = index;

      this.lightbox?.classList.add(
        'is-active'
      );

      document.body.style.overflow =
        'hidden';

      this.update();

      this.updateLightbox();

    }

    /* =====================================================
       LIGHTBOX CLOSE
    ====================================================== */

    closeLightbox() {

      this.lightbox?.classList.remove(
        'is-active'
      );

      document.body.style.overflow =
        '';

    }

    /* =====================================================
       UPDATE LIGHTBOX
    ====================================================== */

    updateLightbox() {

      if (
        !this.lightboxTrack
      ) return;

      const offset =
        -(window.innerWidth *
          this.currentIndex);

      this.lightboxTrack.style.transform =
        `translate3d(${offset}px,0,0)`;

    }

    /* =====================================================
       UPDATE
    ====================================================== */

    update(animated = true) {

      const slideWidth =
        this.viewport.clientWidth;

      const offset =
        -(slideWidth *
          this.currentIndex);

      this.track.style.transition =
        animated
          ? 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)'
          : 'none';

      this.track.style.transform =
        `translate3d(${offset}px,0,0)`;

      /* ===================================================
         ACTIVE THUMB
      ==================================================== */

      this.thumbnails.forEach(
        (thumbnail, index) => {

          thumbnail.classList.toggle(
            'is-active',
            index ===
            this.currentIndex
          );

        }
      );

      this.scrollActiveThumbnailIntoView();

    }

  }

  /* =====================================================
     INIT
  ====================================================== */

  document.addEventListener(
    'DOMContentLoaded',
    () => {

      document
        .querySelectorAll(
          '[data-product-gallery]'
        )
        .forEach((gallery) => {

          if (
            gallery.dataset
              .galleryInitialized
          ) {

            return;

          }

          gallery.dataset.galleryInitialized =
            'true';

          new ProductGallery(gallery);

        });

    }
  );

})();